import autobind from 'autobind-decorator';
import PropTypes from 'prop-types';
import { Component } from 'react';
import Notifications from 'react-notification-system-redux';
import { connect } from 'react-redux';

import { isValidPhoneNumber } from 'react-phone-number-input';
import { bindActionCreators } from 'redux';
import { InputProps } from '../../../../common/components/form-elements/Input';
import * as Actions from '../../../../common/services/customer/Actions';
import * as CustomerService from '../../../../common/services/customer/Service';
import { CustomerActionPropTypes, CustomerError } from '../../../../common/services/customer/Types';
import { CustomerDataDTO } from '../../../../common/types/dto/CustomerDataDTO';
import { UpdateCustomerDTO } from '../../../../common/types/dto/UpdateCustomerDTO';
import { ConfigProp, RenderPropWithProps, TranslationFuncProp } from '../../../../common/types/prop-types/common';
import { RootState } from '../../../base/RootReducer';
import { config } from '../../../common/';
import { AppThunkDispatch } from '../../../common/types';

interface PhoneInputProps extends InputProps {
    onChange: (value: string) => void;
    value: string;
}

export interface CustomerInfoProps extends ConfigProp {
    editState: EditState;
    firstName: InputProps;
    lastName: InputProps;
    address: InputProps;
    addressNumber: InputProps;
    addressFloor: InputProps;
    city: InputProps;
    postalCode: InputProps;
    nif: InputProps;
    phoneNumber: PhoneInputProps;
    submitErrorText: string;
    email?: string | null;
    btnEditCustomerInfo: {
        onClick: (event: React.MouseEvent<{}> | null) => void;
        value: string;
        disabled: boolean;
    };
}

export interface CustomerInfoContainerProps {
}

export interface EditState {
    isEditing: boolean;
    changeEditMode: (event: React.MouseEvent<{}> | null) => void;
}

interface ReduxStateProps {
    isCommunicating: boolean;
    customerInfo: CustomerDataDTO | undefined;
}

interface ReduxDispatchProps {
    customerActions: typeof Actions;
    customerServiceActions: CustomerService.ServiceActions;
    dispatch: AppThunkDispatch;
}

interface CustomerInfoContainerState {
    isEditing: boolean;
    firstName: string;
    firstNameValMsg: string;
    lastName: string;
    lastNameValMsg: string;
    address: string;
    addressValMsg: string;
    addressNumber: string;
    addressNumberValMsg: string;
    addressFloor: string;
    addressFloorValMsg: string;
    city: string;
    cityValMsg: string;
    postalCode: string;
    postalCodeValMsg: string;
    nif: string;
    nifValMsg: string;
    phoneNumber: string;
    phoneNumberValMsg: string;
    submitErrorText: string;
}

class CustomerInfoContainer extends Component<
    CustomerInfoContainerProps &
    TranslationFuncProp &
    RenderPropWithProps<CustomerInfoProps> &
    ReduxStateProps &
    ReduxDispatchProps,
    CustomerInfoContainerState
    > {
    public static propTypes = {
        t: PropTypes.func.isRequired,
        isCommunicating: PropTypes.bool.isRequired,
        customerActions: CustomerActionPropTypes.isRequired,
        customerServiceActions: CustomerService.ServiceActionPropTypes.isRequired,
        customerInfo: PropTypes.any,
        dispatch: PropTypes.func.isRequired,
        //
        children: PropTypes.func.isRequired,
    };

    public readonly state: CustomerInfoContainerState = {
        isEditing: false,
        firstName: '',
        firstNameValMsg: '',
        lastName: '',
        lastNameValMsg: '',
        address: '',
        addressValMsg: '',
        addressNumber: '',
        addressNumberValMsg: '',
        addressFloor: '',
        addressFloorValMsg: '',
        city: '',
        cityValMsg: '',
        postalCode: '',
        postalCodeValMsg: '',
        nif: '',
        nifValMsg: '',
        phoneNumber: '',
        phoneNumberValMsg: '',
        submitErrorText: '',
    };

    public componentDidMount() {
        this.handleInit(this.props);
    }

    public handleInit(nextProps: ReduxStateProps) {
        if (nextProps.customerInfo) {
            this.setState({
                firstName: nextProps.customerInfo.firstName || this.state.firstName,
                lastName: nextProps.customerInfo.lastName || this.state.lastName,
                address: nextProps.customerInfo.address || this.state.address,
                addressNumber: nextProps.customerInfo.addressNumber || this.state.addressNumber,
                addressFloor: nextProps.customerInfo.addressFloor || this.state.addressFloor,
                city: nextProps.customerInfo.city || this.state.city,
                postalCode: nextProps.customerInfo.postalCode || this.state.postalCode,
                nif: nextProps.customerInfo.nif || this.state.nif,
                phoneNumber: nextProps.customerInfo.phoneNumber || this.state.phoneNumber,
            });
        }
    }

    @autobind
    public onFieldKeyPress(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Enter') {
            this.onEditSubmit(null);
        }
    }

    public validateForm() {
        if (this.props.isCommunicating) {
            return false;
        }
        let isValid: boolean = true;
        if (this.state.firstName === '') {
            this.setState({ firstNameValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (config.Constants.Regex.HtmlInjectionValidation.test(this.state.firstName)) {
            this.setState({ firstNameValMsg: this.props.t('signup:errors:htmlInjectionValidationRegex') });
            isValid = false;
        }
        if (this.state.lastName === '') {
            this.setState({ lastNameValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (config.Constants.Regex.HtmlInjectionValidation.test(this.state.lastName)) {
            this.setState({ lastNameValMsg: this.props.t('signup:errors:htmlInjectionValidationRegex') });
            isValid = false;
        }
        if (this.state.address === '') {
            this.setState({ addressValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (config.Constants.Regex.HtmlInjectionValidation.test(this.state.address)) {
            this.setState({ addressValMsg: this.props.t('signup:errors:htmlInjectionValidationRegex') });
            isValid = false;
        }
        if (this.state.addressNumber === '') {
            this.setState({ addressNumberValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (config.Constants.Regex.HtmlInjectionValidation.test(this.state.addressNumber)) {
            this.setState({ addressNumberValMsg: this.props.t('signup:errors:htmlInjectionValidationRegex') });
            isValid = false;
        }
        if (this.state.addressFloor === '') {
            this.setState({ addressFloorValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (config.Constants.Regex.HtmlInjectionValidation.test(this.state.addressFloor)) {
            this.setState({ addressFloorValMsg: this.props.t('signup:errors:htmlInjectionValidationRegex') });
            isValid = false;
        }
        if (this.state.city === '') {
            this.setState({ cityValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (config.Constants.Regex.HtmlInjectionValidation.test(this.state.city)) {
            this.setState({ cityValMsg: this.props.t('signup:errors:htmlInjectionValidationRegex') });
            isValid = false;
        }
        if (this.state.postalCode === '') {
            this.setState({ postalCodeValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (!config.Constants.Regex.PostalCodeValidation.test(this.state.postalCode)) {
            this.setState({ postalCodeValMsg: this.props.t('signup:errors:postalCodeValidationRegex') });
            isValid = false;
        }
        if (this.state.nif !== '' && config.Constants.Regex.HtmlInjectionValidation.test(this.state.nif)) {
            this.setState({ nifValMsg: this.props.t('signup:errors:htmlInjectionValidationRegex') });
            isValid = false;
        }
        if (this.state.phoneNumber === '') {
            this.setState({ phoneNumberValMsg: this.props.t('mandatory') });
            isValid = false;
        } else if (!isValidPhoneNumber(this.state.phoneNumber)) {
            this.setState({ phoneNumberValMsg: this.props.t('signup:errors.phoneNumberInvalidCode') });
            isValid = false;
        }
        return isValid;
    }

    public changeEditMode = (e: React.MouseEvent<{}> | null) => {
        if (e) {
            e.preventDefault();
        }
        this.setState({
            isEditing: !this.state.isEditing,
        });
        this.handleInit(this.props);
    }

    @autobind
    public async onEditSubmit(e: React.MouseEvent<{}> | null) {
        if (e) {
            e.preventDefault();
        }
        if (!this.validateForm()) {
            return;
        }
        try {
            const updateCustomerDTO: UpdateCustomerDTO = {
                firstName: this.state.firstName,
                lastName: this.state.lastName,
                address: this.state.address,
                addressNumber: this.state.addressNumber,
                addressFloor: this.state.addressFloor,
                city: this.state.city,
                postalCode: this.state.postalCode,
                nif: this.state.nif,
                phoneNumber: this.state.phoneNumber,
            };
            await this.props.customerServiceActions.updateCustomer(updateCustomerDTO);
            this.props.dispatch(Notifications.success({
                title: this.props.t('success'),
                message: this.props.t('profile:updateCustomer:success'),
                position: 'tc',
                autoDismiss: 5,
            }));
            this.setState({
                submitErrorText: '',
                isEditing: false,
            });
            await this.props.customerServiceActions.customerData();
        } catch (error) {
            if (error instanceof CustomerError) {
                const exceptionType: string = error.customerErrorInfo.exceptionType || 'UnknownException';
                const exceptionErrorMsg: string = this.props.t(`exception:${exceptionType.split('.').pop()}`);
                this.setState({ submitErrorText: exceptionErrorMsg });
                this.props.dispatch(Notifications.error({
                    title: this.props.t('error'),
                    message: exceptionErrorMsg,
                    position: 'tc',
                    autoDismiss: 5,
                }));
            } else {
                throw error;
            }
        }
    }

    @autobind
    public onFirstNameChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ firstName: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ firstNameValMsg: '' });
        } else {
            this.setState({ firstNameValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onLastNameChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ lastName: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ lastNameValMsg: '' });
        } else {
            this.setState({ lastNameValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onAddressChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ address: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ addressValMsg: '' });
        } else {
            this.setState({ addressValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onAddressNumberChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ addressNumber: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ addressNumberValMsg: '' });
        } else {
            this.setState({ addressNumberValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onAddressFloorChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ addressFloor: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ addressFloorValMsg: '' });
        } else {
            this.setState({ addressFloorValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onCityChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ city: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ cityValMsg: '' });
        } else {
            this.setState({ cityValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onPostalCodeChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({ postalCode: event.currentTarget.value });
        if (event.currentTarget.value !== '') {
            this.setState({ postalCodeValMsg: '' });
        } else {
            this.setState({ postalCodeValMsg: this.props.t('mandatory') });
        }
    }

    @autobind
    public onNifChange(event: React.FormEvent<HTMLInputElement>) {
        this.setState({
            nif: event.currentTarget.value,
            nifValMsg: '',
        });
    }

    @autobind
    public onPhoneNumberChange(value: string) {
        this.setState({ phoneNumber: value });
        if (value !== '') {
            if (isValidPhoneNumber(value)) {
                this.setState({ phoneNumberValMsg: '' });
            } else {
                this.setState({ phoneNumberValMsg: this.props.t('signup:errors.phoneNumberInvalidCode') });
            }
        } else {
            this.setState({ phoneNumberValMsg: this.props.t('mandatory') });
        }
    }

    public render() {
        const newProps: CustomerInfoProps = {
            editState: {
                isEditing: this.state.isEditing,
                changeEditMode: this.changeEditMode,
            },
            firstName: {
                inputProps: {
                    maxLength: config.Validations.FirstName.MaxLength,
                    value: this.state.firstName,
                    onChange: this.onFirstNameChange,
                },
                errorText: this.state.firstNameValMsg,
                hintText: '',
            },
            lastName: {
                inputProps: {
                    maxLength: config.Validations.FirstName.MaxLength,
                    value: this.state.lastName,
                    onChange: this.onLastNameChange,
                },
                errorText: this.state.lastNameValMsg,
                hintText: '',
            },
            address: {
                inputProps: {
                    maxLength: config.Validations.AddressStreet.MaxLength,
                    value: this.state.address,
                    onChange: this.onAddressChange,
                },
                errorText: this.state.addressValMsg,
                hintText: '',
            },
            addressNumber: {
                inputProps: {
                    maxLength: config.Validations.AddressNumber.MaxLength,
                    value: this.state.addressNumber,
                    onChange: this.onAddressNumberChange,
                },
                errorText: this.state.addressNumberValMsg,
                hintText: '',
            },
            addressFloor: {
                inputProps: {
                    maxLength: config.Validations.AddressFloor.MaxLength,
                    value: this.state.addressFloor,
                    onChange: this.onAddressFloorChange,
                },
                errorText: this.state.addressFloorValMsg,
                hintText: '',
            },
            city: {
                inputProps: {
                    maxLength: config.Validations.City.MaxLength,
                    value: this.state.city,
                    onChange: this.onCityChange,
                },
                errorText: this.state.cityValMsg,
                hintText: '',
            },
            postalCode: {
                inputProps: {
                    maxLength: config.Validations.PostalCode.MaxLength,
                    value: this.state.postalCode,
                    onChange: this.onPostalCodeChange,
                },
                errorText: this.state.postalCodeValMsg,
                hintText: '',
            },
            nif: {
                inputProps: {
                    value: this.state.nif,
                    onChange: this.onNifChange,
                    maxLength: config.Validations.Nif.MaxLength,
                },
                errorText: this.state.nifValMsg,
                hintText: '',
            },
            phoneNumber: {
                inputProps: {
                    maxLength: config.Validations.PhoneNumber.MaxLength,
                },
                errorText: this.state.phoneNumberValMsg,
                hintText: '',
                onChange: this.onPhoneNumberChange,
                value: this.state.phoneNumber,
            },
            submitErrorText: this.state.submitErrorText,
            btnEditCustomerInfo: {
                onClick: this.onEditSubmit,
                value: this.props.t('signup:signup'),
                disabled: this.props.isCommunicating,
            },
            email: this.props.customerInfo ? this.props.customerInfo.email : undefined,
            config,
        };

        return this.props.children(newProps);
    }
}

const mapStateToProps = (store: RootState) => ({
    isCommunicating: store.customerState.updateCustomer.isCommunicating,
    customerInfo: store.customerState.customerData.data.customerData,
});

const mapDispatchToProps = (dispatch: AppThunkDispatch) => ({
    customerActions: bindActionCreators(Actions, dispatch),
    customerServiceActions: CustomerService.bindServiceActionCreators(config, dispatch),
    dispatch,
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomerInfoContainer);
